import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { lastValueFrom } from "rxjs";
import { ObservationResponseAllDto } from "../../../../common/dtos/observation/observation.response.all";
import { TranslationDto } from "../../../../common/dtos/translation/translation.dto";
import { TranslationResponseDto } from "../../../../common/dtos/translation/translation.response";
import { Field } from "../../../../shared/components/table/filter/field";
import { ObservationService } from "../../../../shared/services/observation.service";
import { TranslationService } from "../../../../shared/services/translation.service";
import { UserService } from "../../../../shared/services/user.service";
import { Option } from "./../../../../shared/components/table/filter/option";
import { UserDto } from "../../../../common/dtos/user/user.dto";
import { ProjectService } from "../../../../shared/services/project.service";
import { ProjectDto } from "../../../../common/dtos/project/project.dto";
import { UserProjectDto } from "../../../../common/dtos/user-project/user-project.dto";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivityTypeService } from "../../../../shared/services/activity-type.service";
import { ActivityTypeDto } from "../../../../common/dtos/activity-type/activity-type.dto";
import { ReferenceService } from "../../../../shared/services/reference.service";
import { ReferenceDto } from "../../../../common/dtos/reference/reference.dto";

@Component({
  selector: "app-charts",
  templateUrl: "./charts.component.html",
  styleUrls: [
    "./charts.component.scss"
  ]
})
export class ChartsComponent implements OnInit {

  currentFilterSearchString = "";
  sortTable = "insertDate:DESC";
  projects = "";
  public data: ObservationResponseAllDto | undefined;
  isLoading = false;
  lang: TranslationDto | undefined;
  range: FormGroup;
  projectOption!: Option[];
  activityOption!: Option[];
  referenceOption!: Option[];
  user!: UserDto | undefined;
  username = "";
  myControl = new FormControl<{ key: string; value: unknown } | null>(null);
  filters!: Field[];
  idLang: string | undefined;
  isDisabled = false;
  quantity = 0;

  constructor(
        private observationService: ObservationService,
        private translationService: TranslationService,
        private translate: TranslateService,
        private userService: UserService,
        private projectService: ProjectService,
        private snackBar: MatSnackBar,
        private referenceService: ReferenceService,
        private activityTypeService: ActivityTypeService

  ) {
    this.range = new FormGroup({
      start: new FormControl<Date | null>(null),
      end: new FormControl<Date | null>(null)
    });
  }

  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    this.isDisabled = true;
    this.idLang = this.translationService.getLanguages(this.translate.currentLang);

    this.filters = await this.getFilters();
    this.setDefaultDates();
    await this.userService.getUserDto().subscribe((name) => {
      this.user = name!;
    });

    await Promise.all([
      this.getUserProjects(this.user!),
      this.getAllProjects()
    ]);

    this.data = await lastValueFrom(this.observationService.
      getAllObservations(this.generateDateRange(), this.sortTable, this.idLang, this.projects));
    this.quantity = this.data.content.length;
    this.isLoading = false;
    this.isDisabled = false;
  }

  displayFn(option: { key: string; value: unknown } | null): string {
    return option ? option.key : "";
  }

  setDefaultDates(): void {
    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(today.getMonth() - 1);

    this.range.patchValue({
      start: oneMonthAgo,
      end: today
    });
  }

  calculateDateDifference(date1: string, date2: string): number {
    const date1Obj = new Date(date1);
    const date2Obj = new Date(date2);

    const diffInMs = Math.abs(date2Obj.getTime() - date1Obj.getTime());
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    return diffInDays;
  }

  extractDates(input: string): [string, string] | null {
    const dateRegex = /\b(\d{4}-\d{2}-\d{2})\b/g;
    const matches = input.match(dateRegex);

    if (matches && matches.length === 2) {
      return [
        matches[0], matches[1]
      ];
    } else {
      return null;
    }
  }

  async getTraslations(): Promise<TranslationResponseDto> {
    const result = await lastValueFrom(this.translationService.getTranslations());
    return result;
  }

  async getAllProjects(): Promise<Option[]> {
    if (this.user === undefined) {
      const r = await lastValueFrom(this.userService.getUsers("username=" + this.username));
      this.user = r.data[0];
      this.getUserProjects(this.user);
    }
    // eslint-disable-next-line
        const result = await lastValueFrom(this.observationService.getProjectsByUser(this.user?.id!));
    // eslint-disable-next-line
        if (result.user_project?.length == 0) {
      const resultAll = await lastValueFrom(this.projectService.getAllProjects());
      const optionListAll = resultAll?.content!.map((projectItem: ProjectDto) => {
        return {
          key: projectItem?.code?.toString()
                        + " - " + projectItem?.description?.toString()
                        + " - " + projectItem?.country.description,
          value: projectItem?.id
        };
      });
      optionListAll.sort((a, b) => a.key.localeCompare(b.key));
      this.projectOption = optionListAll;
      return optionListAll;
    } else {
      const optionList = result?.user_project!.map((projectItem: UserProjectDto) => {
        return {
          key: projectItem?.project.code?.toString()
                        + " - " + projectItem?.project.description?.toString()
                        + " - " + projectItem?.project.country.description,
          value: projectItem?.project.id.toString()
        };
      });
      optionList.sort((a, b) => a.key.localeCompare(b.key));
      this.projectOption = optionList;
      return optionList;
    }

  }

  async getUserProjects(user: UserDto) {
    await this.observationService.getProjectsByUser(user?.id)
      .subscribe(async(result) => {
        const projectIdString = result.user_project!.map((item) => item.project.id).join(",");
        this.projects = projectIdString;
      });
  }

  generateDateRange(): string {
    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(today.getMonth() - 1);
    const formatDate = (date: Date) =>
    // eslint-disable-next-line
            `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;

    const todayFormatted = formatDate(today);
    const oneMonthAgoFormatted = formatDate(oneMonthAgo);
    return `insertDate]${oneMonthAgoFormatted} ${todayFormatted},`;
  }

  async getActivities(): Promise<Option[]> {

    const result = await lastValueFrom(this.activityTypeService.getActivities(this.idLang!));

    const optionList = result?.content.map((activityItem: ActivityTypeDto) => {
      return {
        key: activityItem?.description?.toString(),
        value: activityItem?.id
      };
    });
    optionList.sort((a, b) => a.key.localeCompare(b.key));
    this.activityOption = optionList;
    return optionList;
  }

  async getFilters(): Promise<Field[]> {
    return [
      {
        id: "insertDate",
        caption: "observationcard.form.insertdate",
        fieldType: "rangedate",
        formControl: new FormControl(""),
        field: "insertDate",
        selectedFilter: "",
        options: []
      },
      {
        id: "project",
        caption: "observationcard.form.project",
        fieldType: "autocomplete",
        formControl: new FormControl(""),
        field: "project",
        selectedFilter: "",
        options: await this.getAllProjects()
      },
      {
        id: "activityType",
        caption: "observationcard.form.activityType",
        fieldType: "autocomplete",
        formControl: new FormControl(""),
        field: "activityType",
        selectedFilter: "",
        options: await this.getActivities()
      },
      {
        id: "status",
        caption: "observationcard.form.status",
        fieldType: "autocomplete",
        formControl: new FormControl(""),
        field: "observationStatus.status",
        selectedFilter: "",
        options: [
          { key: "INSERTED", value: "1" },
          { key: "ASSIGNED", value: "2" },
          { key: "VERIFIED", value: "3" },
          { key: "DISCARDED", value: "4" }
        ]
        // options: //await this.getStatuses()
      },
      /* {
                id: "reference",
                caption: "references.form.classification",
                fieldType: "autocomplete",
                formControl: new FormControl(""),
                field: "observationReference.summary",
                selectedFilter: "",
                options: await this.getReferences()
            },*/
      {
        id: "origin",
        caption: "observationcard.form.origin",
        fieldType: "rangecheckbox",
        formControl: new FormControl(""),
        field: "origin",
        selectedFilter: "",
        options: [
          { key: "phone_android", value: "APP_MOBILE" },
          { key: "qr_code_2", value: "QR_CODE" },
          { key: "language", value: "WEB_APP" }
        ]
      }
    ];
  }

  async getReferences(): Promise<Option[]> {
    const result = await lastValueFrom(this.referenceService.getReferences());
    const optionList = result?.data.map((referenceItem: ReferenceDto) => {
      return {
        key: referenceItem?.summary?.toString(),
        value: referenceItem?.id
      };
    });
    optionList.sort((a, b) => a.key.localeCompare(b.key));
    this.referenceOption = optionList;
    return optionList;

  }

  async onFilterUpdate(searchString: string): Promise<void> {
    this.isDisabled = true;
    this.isLoading = true;
    this.currentFilterSearchString = searchString;
    if (this.range.value && this.currentFilterSearchString.indexOf("insertDate]") < 0) {
      this.data = await lastValueFrom(this.observationService.
        getAllObservations(this.generateDateRange() + this.currentFilterSearchString,
          this.sortTable,
          this.idLang,
          this.projects));
      this.quantity = this.data.content.length;

      this.isDisabled = false;
      this.isLoading = false;
    } else {
      if (this.currentFilterSearchString.indexOf("insertDate]") >= 0) {
        const dateRegex = /\d{4}-\d{2}-\d{2}/g;
        const dates = this.currentFilterSearchString.match(dateRegex);

        if (dates && dates.length >= 2) {
          const momentStart = moment(dates[0]);
          const momentEnd = moment(dates[1]);

          const formattedStartDate = momentStart.format("YYYY-MM-DD");
          const formattedEndDate = momentEnd.format("YYYY-MM-DD");

          const diffInDays = this.calculateDateDifference(formattedStartDate, formattedEndDate);
          if (diffInDays > 31) {
            this.snackBar.open(this.translate.instant("message.export.limit.warning"), undefined, {
              duration: 3000,
              panelClass: "app-notification-success"
            });
            this.isDisabled = false;
            this.isLoading = false;
            return;
          }
        } else {
          this.isDisabled = false;
          this.isLoading = false;
          return;
        }

        this.data = await lastValueFrom(this.observationService.
          getAllObservations(this.currentFilterSearchString, this.sortTable, this.idLang, this.projects));
        this.quantity = this.data.content.length;
        this.isDisabled = false;
        this.isLoading = false;
      } else {
        this.isDisabled = false;
        this.isLoading = false;
      }
    }
  }
}
